import * as React from 'react';
import { resolve } from 'inversify-react';
import { computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import autobind from 'autobind-decorator';

import { ConfigurationService } from 'src/core/services/configuration.service';
import { ContextService } from 'src/core/services/context.service';
import { FormatService } from 'src/core/services/format.service';
import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { ControllerProps } from 'src/router';
import { PolicyRuleDataService } from 'src/app/bookings/policy-rules/policy-rule-form.data';
import { QuoteProductVisibilityPolicyFormView } from 'src/app/bookings/quote-product-visibility-policies/form/quote-product-visibility-policy-form.view';
import { QuoteProductVisibilityPolicyDataService } from 'src/app/bookings/quote-product-visibility-policies/form/quote-product-visibility-policy-form.data';
import { ZuluIcon } from 'src/app/shared/icon';

interface Props extends ControllerProps<{ id: string; }> {
}

interface State {
	breadcrumb: any[];
	defaultLanguage: string;
	mode: ControlMode;
}

@observer
export class QuoteProductVisibilityPolicyFormController extends React.Component<Props, State> {
	@resolve
	private configurationService: ConfigurationService;

	@resolve
	private contextService: ContextService;

	@resolve
	private formatService: FormatService;

	@resolve
	private toast: ToastNotifications;

	@observable
	private dataService: QuoteProductVisibilityPolicyDataService;

	@observable
	private policyRuleDataService: PolicyRuleDataService;

	constructor(props: Readonly<Props>) {
		super(props);

		this.state = {
			breadcrumb: [],
			defaultLanguage: '',
			mode: 'display',
		};
	}

	public componentDidMount(): void {
		this.initialize(this.props.match.params.id);
	}

	public componentWillReceiveProps(nextProps: Readonly<Props>): void {
		if (nextProps.match.params.id !== this.props.match.params.id) {
			this.initialize(nextProps.match.params.id);
		}
	}

	public render(): React.ReactNode {
		if (this.dataService == null || this.dataService.item == null) {
			return null;
		}

		const { mode, breadcrumb } = this.state;

		return (
			<QuoteProductVisibilityPolicyFormView
				model={this.dataService.item}
				title={this.title}
				mode={mode}
				onModeChange={this.handleModeChange}
				save={this.save}
				cancel={this.cancel}
				policyRuleDataService={this.policyRuleDataService}
				breadcrumb={breadcrumb}
				goToCard={this.goToCard}
				setBusy={this.props.setBusy}
				contextService={this.contextService}
			/>
		);
	}

	@computed
	private get title(): string {
		const { defaultLanguage } = this.state;
		if (this.dataService && this.dataService.item && this.dataService.item.dynamicProperties['displayName']) {
			return `Quote Product Visibility Policy ${this.dataService.item.dynamicProperties['displayName'][defaultLanguage]} `;
		}

		return '';
	}

	private async closeItem(key: string, model?: Model): Promise<void> {
		const errors = await model.validate(false);
		if (errors.length > 0) {
			this.toast.showToast('Validation error!', errors.map(e => e.text), 'error');
			return;
		}

		const { breadcrumb } = this.state;
		let index = breadcrumb.findIndex(i => i.key === key);
		if (index != -1) {
			index = index === 1 ? 0 : index;
			this.setState({
				breadcrumb: breadcrumb.slice(0, index)
			});
		}
	}

	private initialize(id: string): void {
		this.props.setBusy(true);
		this.loadConfiguration()
			.then(() => this.loadData(id))
			.catch((error) => {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			})
			.then(() => {
				this.props.setBusy(false);
			});
	}

	private async loadConfiguration(): Promise<void> {
		const appConfig = await this.configurationService.current();
		this.setState({ defaultLanguage: appConfig.defaultLanguage });
	}

	private async loadData(id: string): Promise<void> {
		this.dataService = QuoteProductVisibilityPolicyDataService.make(this.contextService);
		this.policyRuleDataService = PolicyRuleDataService.make(this.contextService, this.formatService);

		if (id === 'new') {
			// Create new
			const { defaultLanguage } = this.state;
			await this.dataService.add(defaultLanguage);
			this.handleModeChange('edit');
		} else {
			// Load entity
			const quotePolicyId = parseInt(id);
			this.dataService.addFilter('id', 'eq', quotePolicyId);
			this.dataService.addSorting('id', 'asc', true);
			this.dataService.pageSize = 1;
			await this.dataService.load(query => query);

			this.policyRuleDataService.addFilter('quoteProductVisibilityPolicyId', 'eq', quotePolicyId);
			this.policyRuleDataService.addFilter('isDeleted', 'eq', false);
			this.policyRuleDataService.addSorting('id', 'asc', true);
			this.policyRuleDataService.pageSize = 20;
			await this.policyRuleDataService.load(query => query);
		}
	}

	@autobind
	private handleModeChange(mode: ControlMode): void {
		this.setState({ mode });
	}

	@autobind
	private async goToCard(text: any, key: string, model: Model): Promise<void> {
		const errors = await this.dataService.item.validate(false);
		if (errors.length > 0) {
			this.toast.showToast('Validation error!', errors.map(e => e.text), 'error');
			return;
		}

		const breadcrumb = this.state.breadcrumb.slice();
		if (breadcrumb.length === 0) {
			let textView = <React.Fragment>
				<span>Quote Product Visibility Policy</span>
				<ZuluIcon type='close' />
				<br></br>
				<span>{(this.title) ? this.title : 'New Quote Product Visibility Policy'}</span>
			</React.Fragment>
			breadcrumb.push({
				text: textView,
				key: 'quote-product-visibility-policy',
				breadcrumbName: 'quote-product-visibility-policy',
				onClick: () => { this.closeItem(key, model); },
				item: this.dataService.item
			});
		}

		breadcrumb.push({
			text: text,
			key: key,
			breadcrumbName: key,
			onClick: () => { this.closeItem(key, model); },
			item: model
		});

		this.setState({ breadcrumb });
	}

	@autobind
	private async save(): Promise<void> {
		const item = this.dataService.item;

		const errors = await item.validate(true);
		if (errors.length > 0) {
			this.toast.showToast('Validation error!', errors.map(e => e.text), 'error');
			return;
		}

		this.props.setBusy(true);
		try {
			await this.dataService.save();
			this.toast.showDataSaved();
			this.props.history.replace(`/bookings/quote-product-visibility-policies/${item.entity.id}`);
		} catch (error) {
			this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
		}

		this.props.setBusy(false);
	}

	@autobind
	private cancel(): void {
		this.props.history.push('/bookings/quote-product-visibility-policies');
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/quote-product-visibility-policies/form/quote-product-visibility-policy-form.controller.tsx